import axios, { AxiosResponse, Method } from 'axios';
import configAPIJSON from '../configAPI.json';
import { getImageDimensions } from './utils/getImageDimensions';

const configAPI = JSON.parse(JSON.stringify(configAPIJSON));
const ENDPOINTS = configAPI.endpoints;
interface RequestParams {
  method: Method;
  url: string;
  data?: any;
  headers?: Record<string, string>;
  withCredentials?: boolean;
}

const sendRequest = async ({
  method,
  url,
  data,
  headers,
  withCredentials,
}: RequestParams): Promise<any> => {
  try {
    const res: AxiosResponse<any> = await axios({
      method,
      url,
      data,
      headers,
      withCredentials,
    });
    return res.data;
  } catch (error: any) {
    throw `\nERROR ${error.response.status} \n${JSON.stringify(
      error.response.data
    )}`;
  }
};

// Feedback

export const sendFeedbackMessage = async ({ message }: { message: string }) => {
  const requestData = {
    content: message,
  };
  const decodeFromBase64 = (encodedUrl: string) => atob(encodedUrl);
  const discordWebhookUrlBase64 =
    'aHR0cHM6Ly9kaXNjb3JkLmNvbS9hcGkvd2ViaG9va3MvMTE1MzI1NTU2MDA4NzYxMzQ0MC9fOE5kTW0zMXJjQTA5UmFKLUR0Mk16VVhPNlBLYWwtN0FZakRfeU80WDJTOFpfelNOQ25DWFNlQzVudFlLTU5UalQtSQ==';

  const decodedUrl = decodeFromBase64(discordWebhookUrlBase64);

  await sendRequest({
    method: 'POST',
    url: decodedUrl,
    data: requestData,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// Authorize

export const authorize = async ({ themeID }: { themeID: string }) => {
  const data = await sendRequest({
    method: 'POST',
    url: `${ENDPOINTS.themes}/${themeID}/authorize`,
    withCredentials: true,
  });
  return data.token;
};

// Account

export const loginStatus = async () => {
  const data = await sendRequest({
    method: 'GET',
    url: ENDPOINTS.loginStatus,
    withCredentials: true,
  });

  return {
    email: data.user.username,
    planName: data.user.plan.name,
    paymentModel: data.user.plan.payment_model,
  };
};

export const logout = async () =>
  sendRequest({
    method: 'POST',
    url: ENDPOINTS.logout,
    withCredentials: true,
  });

// Theme

export const createTheme = async () =>
  sendRequest({
    method: 'POST',
    url: `${ENDPOINTS.themesBackend}/themes`,
  });

export const getThemeData = async ({ themeID }: { themeID: string }) =>
  sendRequest({
    method: 'GET',
    url: `${ENDPOINTS.themesBackend}/themes/${themeID}`,
  });

// Version

export const createThemeVersion = async ({
  themeID,
  themeConfig,
  thumbnail,
}: {
  themeID: string;
  themeConfig: any;
  thumbnail: any;
}) => {
  const token = await authorize({ themeID });

  const form = new FormData();
  form.append('themeConfig', JSON.stringify(themeConfig));
  form.append('editorConfig', JSON.stringify({ placeholder: 'placeholder' }));
  form.append('thumbnail', thumbnail, 'versionPreview');
  form.append('launcherVersion', '0.0.0');

  return sendRequest({
    method: 'POST',
    url: `${ENDPOINTS.themesBackend}/themes/${themeID}/versions`,
    data: form,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const getThemeVersions = async ({ themeID }: { themeID: string }) => {
  const data = await sendRequest({
    method: 'GET',
    url: `${ENDPOINTS.themesBackend}/themes/${themeID}/versions`,
  });

  return data.versions;
};

export const getThemeVersionConfig = async ({
  themeID,
  versionID,
}: {
  themeID: string;
  versionID: string;
}) => {
  const data = await sendRequest({
    method: 'GET',
    url: `${ENDPOINTS.themesBackend}/themes/${themeID}/versions/${versionID}`,
  });

  return data.themeConfig;
};

export const deleteThemeVersion = async ({
  themeID,
  versionID,
}: {
  themeID: string;
  versionID: string;
}) => {
  const token = await authorize({ themeID });

  sendRequest({
    method: 'DELETE',
    url: `${ENDPOINTS.themesBackend}/themes/${themeID}/versions/${versionID}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

// Images

export const uploadImage = async ({
  themeID,
  imageData,
}: {
  themeID: string;
  imageData: any;
}) => {
  const token = await authorize({ themeID });
  const dimensions = await getImageDimensions(imageData);

  const form = new FormData();
  form.append('image', imageData, imageData.name);
  form.append(
    'name',
    `${imageData.name} (${dimensions.width}x${dimensions.height})`
  );

  return sendRequest({
    method: 'POST',
    url: `${ENDPOINTS.themesBackend}/themes/${themeID}/images`,
    data: form,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const getImages = async ({ themeID }: { themeID: string }) => {
  const data = await sendRequest({
    method: 'GET',
    url: `${ENDPOINTS.themesBackend}/themes/${themeID}/images`,
  });

  return data.images;
};

export const deleteImage = async ({
  themeID,
  imgID,
}: {
  themeID: string;
  imgID: string;
}) => {
  const token = await authorize({ themeID });

  return sendRequest({
    method: 'DELETE',
    url: `${ENDPOINTS.themesBackend}/themes/${themeID}/images/${imgID}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};
